import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import { ReactComponent as IconCloseBlack } from '../../../assets/icons/ic-close-black.svg';
import { ReactComponent as IconHamberger } from '../../../assets/icons/ic-hamberger.svg';

const Filter = ({ codeFilter, defaultCode, handleSetFilter, onClose }) => {
    const { t } = useTranslation()
    const [chkAll, setChkAll] = useState(false);
    const [chkFilter, setChkFilter] = useState([]);
    const [sortedOrder, setSortedOrder] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [disabled, setDisabled] = useState(false);

    // 개별 항목 선택/해제 처리
    const handleItemCheck = (itemKey) => {
        if (isDragging) return;
        setChkFilter(prev => {
            if (prev.includes(itemKey)) {
                return prev.filter(key => key !== itemKey);
            } else {
                return [...prev, itemKey];
            }
        });
    };

    // 전체 선택/해제 처리
    const handleAllCheck = () => {
        if (isDragging) return;
        if (chkAll) {
            setChkFilter([]);
        } else {
            const activeKeys = Array.isArray(codeFilter) ? codeFilter : [];            
            const inactiveKeys = Array.isArray(defaultCode)
            ? defaultCode.filter(item =>
                !activeKeys.includes(item)
            ).map(item => item) : [];
            setChkFilter([...activeKeys, ...inactiveKeys]);           
            if (sortedOrder.length === 0) setSortedOrder([...activeKeys, ...inactiveKeys]);
        }
    };

    // Drag 함수 수정
    const onDragEnd = (result) => {
        setIsDragging(false);

        const { source, destination } = result;
        if (!destination) return;

        // 전체 리스트(displayOrder) 기준으로 순서 변경
        const newOrder = Array.from(displayOrder);
        const [movedItem] = newOrder.splice(source.index, 1);
        newOrder.splice(destination.index, 0, movedItem);

        setSortedOrder(newOrder);

        // 선택된 항목(chkFilter)도 새 순서에 맞게 재정렬
        const newChkFilter = newOrder.filter(key => chkFilter.includes(key));
        setChkFilter(newChkFilter);
    };

    const onDragStart = () => {
        setIsDragging(true);
    };

    // 최초 접근 시 codeFilter로 초기화
    useEffect(() => {
        // codeFilter 기준으로 먼저 세팅
        const activeKeys = Array.isArray(codeFilter) ? codeFilter : [];
        // defaultCode에서 codeFilter에 없는 항목만 추출해서 뒤에 추가
        const inactiveKeys = Array.isArray(defaultCode)
            ? defaultCode.filter(item =>
                !activeKeys.includes(item.key || item.id || item.code || item)
            ).map(item => item.key || item.id || item.code || item)
            : [];                
        setSortedOrder([...activeKeys, ...inactiveKeys]);
        setChkFilter(activeKeys); // 활성화 항목만 체크
    }, [codeFilter, defaultCode]);

    // chkFilter 변경될 때마다 전체 선택 상태 업데이트
    useEffect(() => {
        const allChecked = Array.isArray(defaultCode) &&
            defaultCode.length > 0 &&
            defaultCode.every(item => chkFilter.includes(item));
        setChkAll(allChecked);
    }, [chkFilter, defaultCode]);

    // displayOrder는 sortedOrder가 있으면 그걸 사용, 없으면 codeFilter 기준
    const displayOrder = useMemo(() => {
        console.log('[displayOrder] sortedOrder :',sortedOrder);
        if (sortedOrder.length > 0) return sortedOrder;
        if (!codeFilter || !Array.isArray(codeFilter)) return [];
        return codeFilter;
    }, [sortedOrder, codeFilter]);

    useEffect(() => {
        setDisabled(!chkFilter.length);
    }, [chkFilter]);

    return (
        <div className="absolute w-[328px] h-[180px] top-1/4 content-center whitespace-pre-line" style={{ background: 'rgba(0,0,0,0.3)' }}>
            <div className="mx-auto bg-[#DEDEDE] text-center flex flex-col rounded-[8px]">
                <div className="flex h-[38px] bg-[#CECECE] pl-[20px] pt-[12px] pr-[12px] pb-[8px] justify-between items-center flex-shrink-0 self-stretch rounded-tl-[8px] rounded-tr-[8px]">
                    <div className="text-[#000]">{t('trend.selectItems')}</div>
                    <IconCloseBlack className="ml-auto cursor-pointer" onClick={onClose} />
                </div>

                <div className="w-full flex-1 flex items-start flex-col bg-[#DEDEDE] px-[20px]">
                    <p className='w-full text-left text-[#000] pt-[20px] pb-[16px] mb-[16px] border-b border-[#CECECE] '>{t('trend.selectArrhythmia')}</p>

                    <div className='flex items-center gap-[4px] mb-[6px]'>
                        <input
                            type="checkbox"
                            className='!bg-[#fff] !border-[#DEDEDE]'
                            checked={chkAll}
                            onChange={handleAllCheck}
                        />
                        <label className="text-[#444] text-[14px]">{t('trend.all')}</label>
                    </div>

                    <div className='w-full flex items-start flex-col bg-[#fff] mb-[6px] border border-[#CECECE] rounded-[4px] max-h-[340px] scrollbar scrollbar--gray'>
                        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                            <Droppable droppableId="arrhythmia-filter">
                                {(provided) => (
                                    <div
                                        className="w-full flex flex-col"
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {displayOrder.map((itemKey, idx) => {                                            
                                            const itemLabel = itemKey;
                                            const isSelected = chkFilter.includes(itemKey);

                                            if (isSelected) {
                                                return (
                                                    <Draggable
                                                        key={itemKey}
                                                        draggableId={itemKey}
                                                        index={idx}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={`w-full flex justify-between px-[12px] py-[4px]
                                                                    transition-colors duration-200
                                                                    bg-[#DEEBFE]
                                                                    ${idx == 0 ? 'rounded-tl-md rounded-tr-md' : ''}
                                                                    ${idx == displayOrder.length - 1 ? 'rounded-bl-md rounded-br-md' : ''}
                                                                `}
                                                                style={{
                                                                    ...provided.draggableProps.style,
                                                                    boxShadow: 'inset 0 0 0 1px #3182F7'
                                                                }}
                                                            >
                                                                <div className='flex items-center gap-[4px]'>
                                                                    <input
                                                                        type="checkbox"
                                                                        className='!bg-[#fff] !border-[#DEDEDE]'
                                                                        checked={true}
                                                                        onChange={() => handleItemCheck(itemKey)}
                                                                    />
                                                                    <label className="text-[#444] text-[14px] select-none">
                                                                        {itemKey}
                                                                    </label>
                                                                </div>
                                                                <div
                                                                    className='flex items-center cursor-grab active:cursor-grabbing'
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <IconHamberger />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            } else {
                                                // 선택되지 않은 항목: 드래그 불가, 비활성화 스타일
                                                return (
                                                    <div
                                                        key={`unselected-${itemKey}`}
                                                        className={`w-full flex justify-between px-[12px] py-[4px] bg-white opacity-60 cursor-not-allowed ${
                                                            idx < displayOrder.length - 1 ? 'border-b border-[#DEDEDE]' : ''
                                                        }`}
                                                    >
                                                        <div className='flex items-center gap-[4px]'>
                                                            <input
                                                                type="checkbox"
                                                                className='!bg-[#fff] !border-[#DEDEDE]'
                                                                checked={false}
                                                                onChange={() => handleItemCheck(itemKey)}
                                                            />
                                                            <label className="text-[#444] text-[14px] select-none">
                                                                {itemLabel}
                                                            </label>
                                                        </div>
                                                        <div className='flex items-center text-gray-400'>
                                                            <IconHamberger />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </div>

                <div className="flex items-center justify-end bg-[#DEDEDE] px-[20px] py-[12px] rounded-bl-[8px] rounded-br-[8px]">
                    <div className="flex items-center justify-center w-[88px] h-[28px] px-[8px] py-[7px] mr-[8px]  gap-[8px] rounded-[6px] border border-[#B8B8B8] bg-gradient-to-b from-[#FFF] to-[#DEDEDE] cursor-pointer text-[#000] text-center font-pretendard text-[16px] font-light leading-[110%]" onClick={onClose}>{t('trend.close')}</div>
                    <div className={`${disabled ? 'opacity-50' : 'cursor-pointer'} flex items-center justify-center w-[88px] h-[28px] px-[8px] py-[7px] gap-[8px] rounded-[6px] border border-[#5E5E5E] bg-gradient-to-b from-[#5E5E5E] to-[#383838] text-[#FFF] text-center font-pretendard text-[16px] font-light leading-[110%]`} onClick={!disabled ? () => { handleSetFilter(chkFilter) } : null} >{t('trend.apply')}</div>
                </div>
            </div>
        </div>
    );
};

export default Filter;